if (!customElements.get('why-super-dirt-carousel')) {
  class WhySuperDirtCarousel extends HTMLElement {
    static positions = new Map();

    connectedCallback() {
      this.track = this.querySelector('[data-why-track]');
      this.slides = [...this.querySelectorAll('[data-why-slide]')];
      this.dots = [...this.querySelectorAll('[data-why-dot]')];
      this.storageKey = `why-super-dirt:${this.dataset.sectionId}`;
      this.sliderQuery = window.matchMedia('(max-width: 1024px)');
      this.currentIndex = this.readPosition();
      this.frame = null;

      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.whyDot), true));
      });
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.sliderQuery.addEventListener('change', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      requestAnimationFrame(() => this.goTo(this.currentIndex, false));
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.sliderQuery?.removeEventListener('change', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    readPosition() {
      let storedPosition = WhySuperDirtCarousel.positions.get(this.storageKey);
      try {
        storedPosition = Number(sessionStorage.getItem(this.storageKey) ?? storedPosition ?? 0);
      } catch {
        storedPosition = Number(storedPosition ?? 0);
      }

      return Math.min(Math.max(storedPosition, 0), Math.max(this.slides.length - 1, 0));
    }

    rememberPosition(index) {
      WhySuperDirtCarousel.positions.set(this.storageKey, index);
      try {
        sessionStorage.setItem(this.storageKey, String(index));
      } catch {
        // In-memory state still preserves the slide during theme-editor reloads.
      }
    }

    goTo(index, smooth) {
      if (!this.slides.length) return;
      this.currentIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.rememberPosition(this.currentIndex);
      this.updateState();

      if (!this.sliderQuery.matches || !this.track) return;
      this.track.scrollTo({
        left: this.currentIndex * this.track.clientWidth,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    handleScroll() {
      if (!this.sliderQuery.matches || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
        if (index === this.currentIndex) return;
        this.currentIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
        this.rememberPosition(this.currentIndex);
        this.updateState();
      });
    }

    handleResize(event) {
      if (event.matches) requestAnimationFrame(() => this.goTo(this.currentIndex, false));
      this.updateState();
    }

    handleBlockSelect(event) {
      const selectedBlock = event.target.closest('[data-block-id]');
      if (!selectedBlock || !this.contains(selectedBlock)) return;
      const index = this.slides.indexOf(selectedBlock);
      if (index >= 0) this.goTo(index, true);
    }

    updateState() {
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === this.currentIndex));
      });

      this.slides.forEach((slide, index) => {
        if (this.sliderQuery.matches) {
          slide.setAttribute('aria-hidden', String(index !== this.currentIndex));
        } else {
          slide.removeAttribute('aria-hidden');
        }
      });
    }
  }

  customElements.define('why-super-dirt-carousel', WhySuperDirtCarousel);
}
